/**
 * AvailableBlocksSection — pre-generation "available blocks" whitelist (UXR C7, Q4).
 *
 * Two renderings of ONE value (`AvailableBlocksValue`):
 * - `variant="chips"` (default) — the legacy chip grid (edit mode and any other
 *   caller that still mounts the section inside a form);
 * - `variant="table"` — create-wizard step 7 (W12 Part B, Task B5): two
 *   `SelectionTable`s (the ten built-in block types, then the project's own
 *   subtypes), the wizard's ONE selection recipe. No chip grids there.
 *
 * Everything is ON by default; structural types (welcome, user_goals,
 * plan_paywall) are locked because every generated strategy needs them — the
 * server force-includes them anyway, so the UI never lies about the outcome.
 * Subtypes disabled in the subtype manager can never be selected.
 */

import React from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';
import {
  STRATEGY_BLOCK_TYPE_CATALOG,
  STRATEGY_STRUCTURAL_BLOCK_TYPES,
} from '@modules/shared-types';
import type { BlockSubtype } from '@modules/firebase-core';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import { SelectionTable, type SelectionRow } from './strategyWizard/SelectionTable';

const BLOCK_TYPE_LABELS: Record<string, string> = {
  welcome: 'Welcome',
  user_goals: 'User Goals',
  user_data: 'User Data',
  plan_paywall: 'Plan & Paywall',
  history: 'History',
  motivation: 'Motivation',
  environment: 'Environment',
  habits: 'Habits',
  strategy: 'Strategy',
  custom: 'Custom',
};

/**
 * What each block type is FOR — the generator's own block functions
 * (`n8n/onboardingAssetPrompts/policy.json` → `blockFunctions[type].primary`),
 * so the table describes what the AI actually plans with, not marketing copy.
 */
const BLOCK_TYPE_PURPOSES: Record<string, string> = {
  welcome: 'First-impression trust and relevance',
  user_goals: 'Goal clarity and segmentation',
  user_data: 'Value exchange before data collection',
  plan_paywall: 'Value demonstration and conversion readiness',
  history: 'Empathetic acknowledgement of past friction',
  motivation: 'Motivation and perceived probability of success',
  environment: 'Real-world fit',
  habits: 'Behaviour context and objection handling',
  strategy: 'Co-created plan confidence',
  custom: 'Supports the block hypothesis',
};

const STRUCTURAL = new Set<string>(STRATEGY_STRUCTURAL_BLOCK_TYPES);

/** Named once — the row detail, the chip tooltip and the disabled reason agree. */
const ALWAYS_ON = 'always on';
const SUBTYPE_DISABLED_REASON = 'Disabled in the subtype manager';

export interface AvailableBlocksValue {
  blockTypes: string[];
  subtypeSlugs: string[];
}

export function defaultAvailableBlocks(subtypes: BlockSubtype[]): AvailableBlocksValue {
  return {
    blockTypes: [...STRATEGY_BLOCK_TYPE_CATALOG],
    subtypeSlugs: subtypes.filter((s) => s.enabled).map((s) => s.slug),
  };
}

/** True when the selection equals the all-on default → the request omits the field (AC-047). */
export function isDefaultAvailableBlocks(value: AvailableBlocksValue, subtypes: BlockSubtype[]): boolean {
  const defaults = defaultAvailableBlocks(subtypes);
  return value.blockTypes.length === defaults.blockTypes.length
    && defaults.blockTypes.every((type) => value.blockTypes.includes(type))
    && value.subtypeSlugs.length === defaults.subtypeSlugs.length
    && defaults.subtypeSlugs.every((slug) => value.subtypeSlugs.includes(slug));
}

export interface AvailableBlocksSectionProps {
  subtypes: BlockSubtype[];
  value: AvailableBlocksValue;
  onChange: (value: AvailableBlocksValue) => void;
  disabled?: boolean;
  /** `'chips'` = the legacy grid (default); `'table'` = create-wizard step 7. */
  variant?: 'chips' | 'table';
}

export const AvailableBlocksSection: React.FC<AvailableBlocksSectionProps> = (props) => (
  props.variant === 'table'
    ? <AvailableBlocksTable {...props} />
    : <AvailableBlocksChips {...props} />
);

// ───────────────────────────── table variant ──────────────────────────────

interface BlockTypeRow extends SelectionRow { structural: boolean }
interface SubtypeRow extends SelectionRow { enabled: boolean }

const AvailableBlocksTable: React.FC<AvailableBlocksSectionProps> = ({
  subtypes,
  value,
  onChange,
  disabled = false,
}) => {
  const typeRows = React.useMemo<BlockTypeRow[]>(() => STRATEGY_BLOCK_TYPE_CATALOG.map((type) => {
    const structural = STRUCTURAL.has(type);
    const purpose = BLOCK_TYPE_PURPOSES[type] ?? '';
    return {
      id: type,
      name: BLOCK_TYPE_LABELS[type] ?? type,
      // Structural rows say WHY they cannot be unticked, right where they are.
      detail: structural ? `${purpose} · ${ALWAYS_ON}` : purpose,
      locked: structural,
      structural,
    };
  }), []);

  // A subtype has no parent block type — its SLUG is what the whitelist
  // carries, so that is what the row shows next to the name.
  const subtypeRows = React.useMemo<SubtypeRow[]>(() => subtypes.map((subtype) => ({
    id: subtype.slug,
    name: subtype.name,
    detail: subtype.enabled
      ? subtype.slug
      : `${subtype.slug} · ${SUBTYPE_DISABLED_REASON.toLowerCase()}`,
    enabled: Boolean(subtype.enabled),
  })), [subtypes]);

  const enabledSubtypeCount = subtypeRows.filter((row) => row.enabled).length;

  return (
    <Stack spacing={3} data-testid={STRATEGY_LIST.availableBlocksSection}>
      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">Block types</Typography>
        <SelectionTable<BlockTypeRow>
          rows={typeRows}
          selectedIds={value.blockTypes}
          onSelectedIdsChange={(blockTypes) => onChange({ ...value, blockTypes })}
          disabled={disabled}
          searchPlaceholder="Search block types"
          summary={`${value.blockTypes.length} of ${typeRows.length} types allowed · ${STRATEGY_STRUCTURAL_BLOCK_TYPES.length} structural ${ALWAYS_ON}`}
          aria-label="Block types"
          data-testid={STRATEGY_LIST.wizardBlockTypesTable}
        />
      </Stack>

      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">Project subtypes</Typography>
        <SelectionTable<SubtypeRow>
          rows={subtypeRows}
          selectedIds={value.subtypeSlugs}
          onSelectedIdsChange={(subtypeSlugs) => onChange({ ...value, subtypeSlugs })}
          disabled={disabled}
          // A subtype switched off in the manager can never reach the request.
          rowSelectable={(row) => row.enabled}
          rowDisabledReason={(row) => (row.enabled ? undefined : SUBTYPE_DISABLED_REASON)}
          searchPlaceholder="Search subtypes"
          summary={`${value.subtypeSlugs.length} of ${enabledSubtypeCount} project subtypes allowed`}
          emptyState={(
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No project subtypes yet — create them from the Block Templates panel gear on the
              strategy canvas.
            </Typography>
          )}
          aria-label="Project subtypes"
          data-testid={STRATEGY_LIST.wizardSubtypesTable}
        />
      </Stack>
    </Stack>
  );
};

// ───────────────────────────── chips variant ──────────────────────────────

const AvailableBlocksChips: React.FC<AvailableBlocksSectionProps> = ({
  subtypes,
  value,
  onChange,
  disabled = false,
}) => {
  const toggleType = (type: string) => {
    if (disabled || STRUCTURAL.has(type)) return;
    const on = value.blockTypes.includes(type);
    onChange({
      ...value,
      blockTypes: on
        ? value.blockTypes.filter((item) => item !== type)
        : [...value.blockTypes, type],
    });
  };

  const toggleSlug = (slug: string) => {
    if (disabled) return;
    const on = value.subtypeSlugs.includes(slug);
    onChange({
      ...value,
      subtypeSlugs: on
        ? value.subtypeSlugs.filter((item) => item !== slug)
        : [...value.subtypeSlugs, slug],
    });
  };

  const enabledSubtypes = subtypes.filter((subtype) => subtype.enabled);
  const disabledSubtypes = subtypes.filter((subtype) => !subtype.enabled);

  return (
    <Box data-testid={STRATEGY_LIST.availableBlocksSection} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body2" color="text.secondary">
        The strategist may only use the selected block types and subtypes. Structural blocks are
        always available — every strategy needs an entry and a paywall close.
      </Typography>
      <Chip
        size="small"
        variant="outlined"
        data-testid="strategy-available-blocks-count"
        label={`${value.blockTypes.length} types · ${value.subtypeSlugs.length} subtypes selected`}
        sx={{ alignSelf: 'flex-start' }}
      />

      <Typography variant="overline" sx={{ fontSize: '10px', lineHeight: 1.5 }}>Block types</Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {STRATEGY_BLOCK_TYPE_CATALOG.map((type) => {
          const on = value.blockTypes.includes(type);
          const locked = STRUCTURAL.has(type);
          const chip = (
            <Chip
              key={type}
              size="small"
              label={BLOCK_TYPE_LABELS[type] ?? type}
              icon={locked ? <LockIcon sx={{ fontSize: 12 }} /> : on ? <CheckIcon sx={{ fontSize: 12 }} /> : undefined}
              color={on ? 'primary' : 'default'}
              variant={on ? 'outlined' : 'outlined'}
              onClick={() => toggleType(type)}
              data-testid={`strategy-available-block-chip-${type}`}
              sx={{
                opacity: on ? 1 : 0.55,
                textDecoration: on ? 'none' : 'line-through',
                cursor: locked || disabled ? 'default' : 'pointer',
              }}
            />
          );
          return locked
            ? <Tooltip key={type} title="Required for strategy structure — always available">{chip}</Tooltip>
            : chip;
        })}
      </Stack>

      <Typography variant="overline" sx={{ fontSize: '10px', lineHeight: 1.5 }}>Project subtypes</Typography>
      {enabledSubtypes.length === 0 && disabledSubtypes.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No project subtypes yet — create them from the Block Templates panel gear on the strategy canvas.
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {enabledSubtypes.map((subtype) => {
            const on = value.subtypeSlugs.includes(subtype.slug);
            return (
              <Chip
                key={subtype.slug}
                size="small"
                label={`${subtype.name} · ${subtype.slug}`}
                icon={on ? <CheckIcon sx={{ fontSize: 12 }} /> : undefined}
                color={on ? 'primary' : 'default'}
                variant="outlined"
                onClick={() => toggleSlug(subtype.slug)}
                data-testid={`strategy-available-block-chip-${subtype.slug}`}
                sx={{
                  opacity: on ? 1 : 0.55,
                  textDecoration: on ? 'none' : 'line-through',
                  cursor: disabled ? 'default' : 'pointer',
                }}
              />
            );
          })}
          {disabledSubtypes.map((subtype) => (
            <Tooltip key={subtype.slug} title={SUBTYPE_DISABLED_REASON}>
              <Chip
                size="small"
                label={`${subtype.name} · ${subtype.slug}`}
                variant="outlined"
                data-testid={`strategy-available-block-chip-${subtype.slug}`}
                sx={{ opacity: 0.4, textDecoration: 'line-through' }}
              />
            </Tooltip>
          ))}
        </Stack>
      )}
    </Box>
  );
};
